import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import {
    IPrikazSplynuti,
    IPrikazSplynutiVznik,
    PrikazSplynutiGet,
    PrikazSplynutiPut
} from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';

import { isIsinDlt } from '../NovyDokument';
import { getDataIsins } from './Zruseni';

interface ISplynuti {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const PrikazSplynuti: React.FC<ISplynuti> = ({ nextStep, zadostID, prikazyData }) => {
    const [form] = Form.useForm();
    const { dokumenty } = useContext(Context);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [refreshIsins, rerender] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<IPrikazSplynuti>();
    const [zanikIsins, SetZanikIsins] = useState<{ isin?: string }[]>([
        {
            isin: undefined
        }
    ]);
    const [vznikIsins, SetVznikIsins] = useState<IPrikazSplynutiVznik[]>([{}]);
    const [dataIsins, setDataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        getDataIsins(dokumenty).map(e => {
            return {
                labelcz: e.isin,
                value: e.isin
            };
        }) || []
    );

    const [selectedIsin, setSelectIsin] = useState<string>();
    const [isDLT, setIsDLT] = useState<boolean>();
    useEffect(() => {
        if (selectedIsin) {
            setIsDLT(isIsinDlt(selectedIsin, dokumenty!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    useEffect(() => {
        setTimeout(() => {
            const fieldsvals = form.getFieldsValue();
            const selectedIsins = [
                ...vznikIsins.map((_e, i) => fieldsvals[`isin${i}`]),
                ...zanikIsins.map((_e, i) => fieldsvals[`zanikISIN${i}`])
            ];
            setDataIsins(
                getDataIsins(dokumenty)
                    .filter(e => !selectedIsins.includes(e.isin))
                    .map(e => {
                        return {
                            labelcz: e.isin,
                            value: e.isin
                        };
                    }) || []
            );
        }, 200);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshIsins]);

    useEffect(() => {
        setLoading(true);
        PrikazSplynutiGet(zadostID)
            .then(resp => {
                setData(resp);
                SetZanikIsins(
                    resp.zanik.length
                        ? resp.zanik
                        : [
                              {
                                  isin: undefined
                              }
                          ]
                );
                setSelectIsin(resp.zanik[0].isin);
                SetVznikIsins(resp.vznik.length ? resp.vznik : [{}]);
            })
            .catch(() => setData({} as IPrikazSplynuti))
            .finally(() => setLoading(false));
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {data && prikazyData && (
                <FormWrapper
                    outForm={form}
                    onFinish={values => {
                        setLoading(true);
                        const dataToSend = {
                            datum: values.datum.format('YYYY-MM-DD'),
                            // nahrsezakc: values.nahrsezakc === 'true',
                            vznik: vznikIsins.map((_e, i) => {
                                return {
                                    isin: values[`isin${i}`],
                                    navakcie: values[`navakcie${i}`],
                                    celkhodnota: values[`celkhodnota${i}`],
                                    pocetcpp: values[`pocetcpp${i}`],
                                    pocetcpn: values[`pocetcpn${i}`]
                                };
                            }),
                            zanik: zanikIsins.map((_e, i) => {
                                return {
                                    isin: values[`zanikISIN${i}`]
                                };
                            })
                        };

                        PrikazSplynutiPut(zadostID, dataToSend as IPrikazSplynuti)
                            .then(() => nextStep())
                            .finally(() => setLoading(false));
                    }}
                >
                    <Title text="EMITENT" level={5} />
                    <InputComponent
                        disabled={true}
                        initialValue={prikazyData?.spolecnost.idmb}
                        label="IDENTIFIKACNI_CISLO"
                        name="idmbSpolecnost"
                        notRequired={true}
                    />
                    {prikazyData?.spolecnost.dicdph && (
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.dicdph}
                            label="ISIN_IDENTIFIKACE_DIC"
                            name="dicdphSpolecnost"
                            notRequired={true}
                        />
                    )}
                    <InputComponent
                        disabled={true}
                        initialValue={prikazyData?.spolecnost.nazev}
                        label="ISIN_IDENTIFIKACE_FIRMA"
                        name="nazevSpolecnost"
                        notRequired={true}
                    />
                    <InputComponent
                        disabled={true}
                        initialValue={prikazyData?.spolecnost.lei}
                        label="LEI_CELE"
                        name="leiSpolecnost"
                        notRequired={true}
                    />
                    <DatePickerComponent
                        name="datum"
                        initialValue={data?.datum ? dayjs(data.datum) : undefined}
                        // disabledDate={current => current < dayjs().add(6, 'days')}
                        label="PRIKAZ_PREVOD_DATUM"
                    />
                    {(zanikIsins || []).map((e, i) => (
                        <SelectComponent
                            name={`zanikISIN${i}`}
                            key={`zanikISIN${i}`}
                            labelParagraph={'PRIKAZ_SPLYNUTI_ZANIKAJICI_ISIN'}
                            defaultValue={e?.isin}
                            isFormItem={true}
                            data={dataIsins}
                            labelExtra={
                                i === zanikIsins.length - 1 || i === 0 ? (
                                    <Button
                                        id="c2PlusAltBtn"
                                        htmlType="button"
                                        type="primary"
                                        style={{
                                            display: 'inline-block',
                                            marginLeft: 20,
                                            width: 50
                                        }}
                                        defaultValue={e.isin}
                                        disabled={false}
                                        icon={i === 0 ? <PlusOutlined /> : <MinusOutlined />}
                                        onClick={() => {
                                            if (i === 0)
                                                SetZanikIsins(pv => [
                                                    ...pv,
                                                    {
                                                        isin: undefined
                                                    }
                                                ]);
                                            else {
                                                form.setFieldsValue({
                                                    [`zanikISIN${i}`]: undefined
                                                });
                                                SetZanikIsins(pv => pv.filter((_e, index) => i !== index));
                                            }
                                            setTimeout(() => rerender(pv => !pv), 0);
                                        }}
                                    />
                                ) : null
                            }
                            onChange={val => {
                                i === 0 && setSelectIsin(val);
                                rerender(pv => !pv);
                            }}
                        />
                    ))}
                    {vznikIsins?.map((vznik, i) => {
                        return (
                            <div key={`${'vznik'}${i}`}>
                                <Title
                                    level={5}
                                    text="PRIKAZ_SPLYNUTI_NOVY_ISIN"
                                    style={{
                                        display: 'inline-block',
                                        fontWeight: 'bold'
                                    }}
                                    dividerTitle={true}
                                >
                                    {/* i === 0 && (
                                            <ButtonComponent
                                                id="c2PlusAltBtn"
                                                htmlType="button"
                                                type="primary"
                                                style={{
                                                    display: 'inline-block',
                                                    width: 50
                                                }}
                                                icon={<PlusOutlined />}
                                                onClick={() =>
                                                    SetVznikIsins(pv => {
                                                        if (pv) {
                                                            const newState = [...pv];
                                                            newState.push({
                                                                isin: undefined,
                                                                navakcie: undefined,
                                                                celkhodnota: vznikIsins.length
                                                                    ? form.getFieldValue(
                                                                          `pocetcpp${vznikIsins.length - 1}`
                                                                      )
                                                                    : undefined,
                                                                pocetcpp: undefined,
                                                                pocetcpn: undefined
                                                            });
                                                            form.resetFields([
                                                                `isin${pv.length}`,
                                                                `navakcie${pv.length}`,
                                                                `celkhodnota${pv.length}`,
                                                                `pocetcpp${pv.length}`,
                                                                `pocetcpn${pv.length}`
                                                            ]);
                                                            return newState;
                                                        }
                                                        return [{}];
                                                    })
                                                }
                                            />
                                        )}
                                        {i === vznikIsins.length - 1 && vznikIsins.length > 1 && (
                                            <ButtonComponent
                                                id="c2MinusBtn"
                                                htmlType="button"
                                                type="primary"
                                                style={{
                                                    display: 'inline-block',
                                                    width: 50
                                                }}
                                                icon={<MinusOutlined />}
                                                onClick={() =>
                                                    SetVznikIsins(pv => {
                                                        if (pv) {
                                                            const newState = [...pv];
                                                            newState.splice(i, 1);
                                                            return newState;
                                                        }
                                                        return [{}];
                                                    })
                                                }
                                            />
                                        ) */}
                                </Title>
                                <SelectComponent
                                    name={`isin${i}`}
                                    labelParagraph="ISIN"
                                    defaultValue={vznik?.isin}
                                    isFormItem={true}
                                    data={dataIsins}
                                    onChange={() => rerender(pv => !pv)}
                                />
                                <InputNumberComponent
                                    name={`navakcie${i}`}
                                    label="PRIKAZ_SPLYNUTI_HODNOTA"
                                    initialValue={vznik.navakcie}
                                    onChangeCustom={val => {
                                        form.setFieldsValue({
                                            [`celkhodnota${i}`]:
                                                form.getFieldValue(`pocetcpn${i}`) && val
                                                    ? form.getFieldValue(`pocetcpn${i}`) * Number(val)
                                                    : undefined
                                        });
                                        rerender(pv => !pv);
                                    }}
                                    precision={4}
                                />
                                <InputNumberComponent
                                    name={`pocetcpp${i}`}
                                    label="PRIKAZ_SPLYNUTI_POCET"
                                    initialValue={vznik.pocetcpp}
                                    disabled={i > 0}
                                    precision={0}
                                />
                                <InputNumberComponent
                                    name={`pocetcpn${i}`}
                                    label="PRIKAZ_SPLYNUTI_POCET_PO"
                                    initialValue={vznik.pocetcpn}
                                    onChangeCustom={val => {
                                        form.setFieldsValue({
                                            [`celkhodnota${i}`]:
                                                form.getFieldValue(`navakcie${i}`) && val
                                                    ? form.getFieldValue(`navakcie${i}`) * Number(val)
                                                    : undefined
                                        });
                                        rerender(pv => !pv);
                                    }}
                                    precision={0}
                                />
                                <InputNumberComponent
                                    name={`celkhodnota${i}`}
                                    label="PRIKAZ_SPLYNUTI_HODNOTA_PO"
                                    initialValue={vznik.celkhodnota}
                                    disabled={true}
                                    notRequired={true}
                                />
                            </div>
                        );
                    })}
                    {/*  <SelectComponent
                            name="nahrsezakc"
                            labelParagraph="PRIKAZ_SPLYNUTI_AKCIONAR_SEZNAM"
                            isFormItem={true}
                            data={trueFalseSelect}
                            defaultValue={typeof data?.nahrsezakc === 'boolean' ? String(data?.nahrsezakc) : undefined}
                        />*/}
                    <PrilohyTable zadostID={zadostID} required={true} />
                    <Title text="PRIKAZ_SPLYNUTI_PODMINKY_TITLE" level={4} />
                    <Paragraph
                        text={isDLT ? 'PRIKAZ_SPLYNUTI_PODMINKY_DLT_TEXT1' : 'PRIKAZ_SPLYNUTI_PODMINKY_TEXT1'}
                        style={{
                            display: 'block',
                            marginTop: 10,
                            marginBottom: 20
                        }}
                    />
                    <Paragraph
                        text={isDLT ? 'PRIKAZ_SPLYNUTI_PODMINKY_DLT_TEXT1_1' : 'PRIKAZ_SPLYNUTI_PODMINKY_TEXT1_1'}
                        style={{
                            display: 'block',
                            marginLeft: 24
                        }}
                    />
                    <Paragraph
                        text={isDLT ? 'PRIKAZ_SPLYNUTI_PODMINKY_DLT_TEXT1_2' : 'PRIKAZ_SPLYNUTI_PODMINKY_TEXT1_2'}
                        style={{
                            display: 'block',
                            marginLeft: 24
                        }}
                    />
                    <Paragraph
                        text={isDLT ? 'PRIKAZ_SPLYNUTI_PODMINKY_DLT_TEXT1_3' : 'PRIKAZ_SPLYNUTI_PODMINKY_TEXT1_3'}
                        style={{
                            display: 'block',
                            marginLeft: 24
                        }}
                    />
                    <Paragraph
                        text={isDLT ? 'PRIKAZ_SPLYNUTI_PODMINKY_DLT_TEXT2' : 'PRIKAZ_SPLYNUTI_PODMINKY_TEXT2'}
                        style={{
                            display: 'block',
                            marginTop: 20,
                            marginBottom: 10
                        }}
                    />
                    <Row
                        style={{
                            marginTop: 26
                        }}
                    >
                        <CheckboxComponent
                            name="souhlas"
                            checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                            onChange={() => setIsChecked(pv => !pv)}
                            checked={isChecked}
                        />
                    </Row>{' '}
                    <Row className="button-row">
                        <Col>
                            <ButtonComponent
                                buttontext="POKRACOVAT"
                                id="isinPokracovatBtn"
                                disabled={!isChecked}
                                htmlType="submit"
                            />
                        </Col>
                    </Row>
                </FormWrapper>
            )}
        </LoadingWrapper>
    );
};

export default PrikazSplynuti;
